import React, { createContext, useState, useEffect, useCallback } from 'react';
import { getCurrentUser, isAuthenticated, login, logout, register } from '../api/auth';

// Create context
export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuth, setIsAuth] = useState(isAuthenticated());

  // Function to fetch current user data
  const fetchCurrentUser = useCallback(async () => {
    if (!isAuthenticated()) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await getCurrentUser();
      setUser({
        ...response.data,
        ...response.data.data.user
      });
      setIsAuth(true);
    } catch (error) {
      console.error('Error fetching current user:', error);
      setError('Failed to fetch user data');
      setIsAuth(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Effect to fetch user on mount and auth state changes
  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  // Login handler
  const handleLogin = async (credentials) => {
    try {
      setLoading(true);
      const response = await login(credentials);
      setUser(response.data.user);
      setIsAuth(true);
      setError(null);
      return response;
    } catch (error) {
      setError(error.response?.data?.message || 'Login failed');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Register handler
  const handleRegister = async (userData) => {
    try {
      setLoading(true);
      const response = await register(userData);
      // After registration, immediately log in
      if (response.status === 201) {
        await handleLogin({
          email: userData.email,
          password: userData.password,
        });
      }
      return response;
    } catch (error) {
      setError(error.response?.data?.message || 'Registration failed');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Logout handler
  const handleLogout = async () => {
    try {
      setLoading(true);
      await logout();
      setUser(null);
      setIsAuth(false);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Update user data (used after profile updates)
  const updateUser = (updatedUserData) => {
    setUser((prevUser) => ({
      ...prevUser,
      ...updatedUserData,
    }));
  };

  // Context value
  const authContextValue = {
    user,
    isAuth,
    loading,
    error,
    login: handleLogin,
    register: handleRegister,
    logout: handleLogout,
    updateUser,
    refreshUser: fetchCurrentUser,
  };

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;